import type { Build } from '@todesktop/shared';
import { Box, Text } from 'ink';
import { useEffect, useState } from 'react';
import type { ProjectConfig } from '../types';
import ErrorDisplay from './ErrorDisplay';
import Table from './table/Table';
import capitalize from '../utilities/capitalize';
import getBuilds from '../utilities/getBuilds';
import getProjectConfig from '../utilities/projectConfig/getProjectConfig';
import getRelativeDateFromDateString from '../utilities/getRelativeDateFromDateString';
import logForCI from '../utilities/logForCI';
import useInput from '../utilities/useInput';
import useExit from '../utilities/useExit';
import findAppUserId from '../utilities/findAppUserId';
import SyntaxHighlight from './SyntaxHighlight';
import LoadingText from './LoadingText';

export default function ViewBuilds({
  commandUsed,
  configPath,
  count,
  format,
  exit: shouldExitAfterViewingBuilds = false,
}: {
  commandUsed: string;
  configPath?: string;
  count?: number;
  format?: string;
  exit?: boolean;
}) {
  const exit = useExit();
  const [
    {
      builds,
      error,
      hasMoreToLoad,
      isInitialLoadComplete,
      isLoading,
      projectConfig,
      startAfter,
      user,
    },
    setState,
  ] = useState<ViewBuildsState>({
    builds: [],
    error: null,
    hasMoreToLoad: true,
    isInitialLoadComplete: false,
    isLoading: false,
    projectConfig: null,
    startAfter: null,
    user: null,
  });

  useInput((input, key) => {
    if (key.escape) {
      exit();
    }

    if (input === ' ' || key.downArrow || key.pageDown || key.return) {
      if (!isInitialLoadComplete || !hasMoreToLoad) {
        return;
      }

      setState((previousState) => ({
        ...previousState,
        startAfter: builds[builds.length - 1].createdAt,
      }));
    }
  });

  useEffect(() => {
    async function viewBuilds() {
      if (
        error ||
        isLoading ||
        (isInitialLoadComplete && (!hasMoreToLoad || !startAfter))
      ) {
        return;
      }

      if (!['table', 'json'].includes(format!)) {
        setState((previousState) => ({
          ...previousState,
          error: new Error(
            `Invalid output format "${format}". Valid formats are "table" and "json".`,
          ),
        }));
        return;
      }

      setState((previousState) => ({
        ...previousState,
        isLoading: true,
      }));

      let config: ProjectConfig;
      try {
        // This will throw if validation fails:
        config = projectConfig || getProjectConfig(configPath).config;
      } catch (e) {
        setState((previousState) => ({ ...previousState, error: e }));
        return;
      }

      const pageSize = count || 5;
      const fetchedUser = await findAppUserId(config.id);

      getBuilds({
        appId: config.id,
        limit: pageSize + 1,
        startAfter: startAfter ?? undefined,
        userId: fetchedUser.id,
      })
        .then((buildsResult) => {
          setState((previousState) => {
            const stateUpdates: Partial<ViewBuildsState> = {
              builds: [],
              hasMoreToLoad: buildsResult.length > pageSize,
              isInitialLoadComplete: true,
              isLoading: false,
              projectConfig: config,
              startAfter: null,
              user: fetchedUser,
            };

            if (buildsResult.length) {
              stateUpdates.builds = [
                ...previousState.builds,
                ...buildsResult.slice(0, pageSize),
              ];
            }
            return {
              ...previousState,
              ...stateUpdates,
            };
          });
        })
        .catch((e: unknown) => {
          logForCI(e);
          setState((previousState) => ({
            ...previousState,
            error: e,
            isLoading: false,
          }));
        });
    }

    viewBuilds();
  }, [
    error,
    hasMoreToLoad,
    isInitialLoadComplete,
    isLoading,
    projectConfig,
    startAfter,
    configPath,
    format,
    count,
  ]);

  // Exit once all are loaded
  useEffect(() => {
    if (!hasMoreToLoad) {
      setTimeout(exit, 10);
    }
  }, [exit, hasMoreToLoad]);

  // Exit the process once the initial build data has been displayed if user has set the `--exit` flag
  useEffect(() => {
    if (!isLoading && isInitialLoadComplete && shouldExitAfterViewingBuilds) {
      setTimeout(exit, 10);
    }
  }, [exit, isLoading, shouldExitAfterViewingBuilds, isInitialLoadComplete]);

  if (error) {
    return <ErrorDisplay commandUsed={commandUsed} error={error} />;
  }

  if (!isInitialLoadComplete) {
    return <LoadingText />;
  }

  if (builds.length) {
    const formatData = (buildList: Build[]) =>
      buildList.map((build) => ({
        'ID': build.id,
        'Version': build.appVersion || 'n/a',
        'Creation date': getRelativeDateFromDateString(build.createdAt),
        'Status': capitalize(build.status),
        'Release date': build.releasedAt
          ? getRelativeDateFromDateString(build.releasedAt)
          : 'Unreleased',
        'Owner': user ? user.label : 'unknown',
      }));

    const getCellTextProps = ({
      key,
      props,
      value,
    }: {
      key: string;
      props: any;
      value: string;
    }) => {
      const result: Record<string, unknown> = { ...props };

      if (key === 'Status') {
        const status = value.toLowerCase();
        if (status === 'succeeded') {
          result.color = 'green';
        } else if (status === 'failed') {
          result.color = 'red';
        } else if (status === 'cancelled') {
          result.dimColor = true;
        }
      } else if (key === 'Version' && value.toLowerCase() === 'n/a') {
        result.dimColor = true;
      } else if (
        key === 'Release date' &&
        value.toLowerCase() === 'unreleased'
      ) {
        result.dimColor = true;
      }

      return result;
    };

    return (
      <>
        {format === 'table' ? (
          <Table
            getCellTextProps={getCellTextProps}
            data={formatData(builds)}
          />
        ) : null}
        {format === 'json' ? (
          <SyntaxHighlight object={removeAppBuilderLibConfig(builds)} />
        ) : null}
        {!shouldExitAfterViewingBuilds && builds.length > 0 ? (
          <Box>
            <Text color="gray" dimColor>
              {getBuildsStatusText(isLoading, hasMoreToLoad, builds.length)}
            </Text>
          </Box>
        ) : null}
      </>
    );
  } else {
    return <Text>There are no builds yet</Text>;
  }
}

function getBuildsStatusText(
  isLoading: boolean,
  hasMoreToLoad: boolean,
  buildsCount: number,
): string {
  if (isLoading) {
    return 'Loading more...';
  }
  if (hasMoreToLoad) {
    return `Showing the latest ${buildsCount} builds. Press space/down to load more.`;
  }
  return `Showing all (${buildsCount}) builds`;
}

function removeAppBuilderLibConfig(builds: Build[]) {
  // Remove the appBuilderLib config from the builds because it's difficult to display
  // and not very useful to the user.
  return builds.map((build) => {
    if (build.mac && build.mac.appBuilderLibConfig) {
      delete build.mac.appBuilderLibConfig;
    }
    if (build.windows && build.windows.appBuilderLibConfig) {
      delete build.windows.appBuilderLibConfig;
    }
    if (build.linux && build.linux.appBuilderLibConfig) {
      delete build.linux.appBuilderLibConfig;
    }
    return build;
  });
}

interface ViewBuildsState {
  builds: Build[];
  error: unknown;
  hasMoreToLoad: boolean;
  isInitialLoadComplete: boolean;
  isLoading: boolean;
  projectConfig: ProjectConfig | null;
  startAfter: string | null;
  user: { id: string; label: string } | null;
}
